import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Loader2, AlertCircle } from "lucide-react";
import { Flag } from "@/components/Flag";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { BeneficiaryCard } from "@/components/BeneficiaryCard";
import { createApplication, getVerifiedBeneficiariesByCompany, linkExistingBeneficiary } from "@/lib/api";
import { toast } from "sonner";

export default function AddApplication() {
  const navigate = useNavigate();
  
  const [submitting, setSubmitting] = useState(false);
  const [checkingBeneficiaries, setCheckingBeneficiaries] = useState(false);
  const [existingBeneficiaries, setExistingBeneficiaries] = useState([]);
  const [linkDialog, setLinkDialog] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [createdAppId, setCreatedAppId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    application_id: "",
    company_name: "",
    company_number: "",
    country: "UK",
    currency: "GBP",
    loan_amount: "",
    loan_approved: true,
  });
  
  const [errors, setErrors] = useState({});

  // Check for existing beneficiaries when company name changes
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (formData.company_name.length >= 3) {
        try {
          setCheckingBeneficiaries(true);
          const beneficiaries = await getVerifiedBeneficiariesByCompany(formData.company_name);
          setExistingBeneficiaries(beneficiaries);
        } catch (error) {
          setExistingBeneficiaries([]);
        } finally {
          setCheckingBeneficiaries(false);
        }
      } else {
        setExistingBeneficiaries([]);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [formData.company_name]);

  // Update currency when country changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      currency: prev.country === "UK" ? "GBP" : "AUD"
    }));
  }, [formData.country]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.application_id.trim()) {
      newErrors.application_id = "Application ID is required";
    } else if (!/^SF-\d+$/i.test(formData.application_id)) {
      newErrors.application_id = "Format should be SF-XXXX (e.g., SF-5999)";
    }
    
    if (!formData.company_name.trim()) {
      newErrors.company_name = "Company name is required";
    }
    
    if (!formData.loan_amount || parseFloat(formData.loan_amount) <= 0) {
      newErrors.loan_amount = "Valid loan amount is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      
      const result = await createApplication({
        ...formData,
        loan_amount: parseFloat(formData.loan_amount),
      });
      
      toast.success("Application created successfully");
      
      // If there are existing beneficiaries, offer to link
      if (existingBeneficiaries.length > 0) {
        setCreatedAppId(result.id);
        setLinkDialog(true);
      } else {
        navigate(`/workflow/${result.id}`);
      }
    } catch (error) {
      console.error("Failed to create application:", error);
      toast.error(error.response?.data?.detail || "Failed to create application");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLinkBeneficiary = async () => {
    if (!selectedBeneficiary || !createdAppId) {
      navigate(`/workflow/${createdAppId}`);
      return;
    }
    
    try {
      await linkExistingBeneficiary(createdAppId, selectedBeneficiary.beneficiary_id, "admin@simplyfunded.com");
      toast.success("Beneficiary linked to application");
      navigate(`/workflow/${createdAppId}`);
    } catch (error) {
      console.error("Failed to link beneficiary:", error);
      toast.error("Failed to link beneficiary, but application was created");
      navigate(`/workflow/${createdAppId}`);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <div data-testid="add-application-page">
      <PageHeader
        title="Add Application"
        description="Manually create a new loan application"
      />

      <div className="max-w-2xl">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle>New Application</CardTitle>
                <CardDescription>
                  Enter the details for the new loan application
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Application ID */}
              <div className="space-y-2">
                <Label htmlFor="application_id">
                  Application ID (SF Reference) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="application_id"
                  placeholder="SF-5999"
                  value={formData.application_id}
                  onChange={(e) => handleInputChange("application_id", e.target.value.toUpperCase())}
                  className={errors.application_id ? "border-destructive" : ""}
                  data-testid="application-id-input"
                />
                {errors.application_id && (
                  <p className="text-sm text-destructive">{errors.application_id}</p>
                )}
              </div>

              {/* Company Name */}
              <div className="space-y-2">
                <Label htmlFor="company_name">
                  Company Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="company_name"
                  placeholder="ACME Ltd"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange("company_name", e.target.value)}
                  className={errors.company_name ? "border-destructive" : ""}
                  data-testid="company-name-input"
                />
                {errors.company_name && (
                  <p className="text-sm text-destructive">{errors.company_name}</p>
                )}
                {checkingBeneficiaries && (
                  <p className="text-xs text-muted-foreground">Checking for existing accounts...</p>
                )}
                {existingBeneficiaries.length > 0 && (
                  <p className="text-xs text-emerald-600">
                    ✓ {existingBeneficiaries.length} verified bank account(s) found for this company
                  </p>
                )}
              </div>

              {/* Company Number */}
              <div className="space-y-2">
                <Label htmlFor="company_number">Company Number (Optional)</Label>
                <Input
                  id="company_number"
                  placeholder="12345678"
                  value={formData.company_number}
                  onChange={(e) => handleInputChange("company_number", e.target.value)}
                  data-testid="company-number-input"
                />
              </div>

              {/* Country & Currency */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => handleInputChange("country", value)}
                  >
                    <SelectTrigger data-testid="country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UK"><span className="inline-flex items-center gap-2"><Flag code="GB" size={16} /> United Kingdom</span></SelectItem>
                      <SelectItem value="AU"><span className="inline-flex items-center gap-2"><Flag code="AU" size={16} /> Australia</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => handleInputChange("currency", value)}
                  >
                    <SelectTrigger data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GBP">£ GBP</SelectItem>
                      <SelectItem value="AUD">A$ AUD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Loan Amount */}
              <div className="space-y-2">
                <Label htmlFor="loan_amount">
                  Loan Amount <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {formData.currency === "GBP" ? "£" : "A$"}
                  </span>
                  <Input
                    id="loan_amount"
                    type="number"
                    placeholder="50000"
                    value={formData.loan_amount}
                    onChange={(e) => handleInputChange("loan_amount", e.target.value)}
                    className={`pl-8 ${errors.loan_amount ? "border-destructive" : ""}`}
                    data-testid="loan-amount-input"
                  />
                </div>
                {errors.loan_amount && (
                  <p className="text-sm text-destructive">{errors.loan_amount}</p>
                )}
              </div>

              {/* Loan Approved */}
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="loan_approved"
                  checked={formData.loan_approved}
                  onCheckedChange={(checked) => handleInputChange("loan_approved", checked)}
                  data-testid="loan-approved-checkbox"
                />
                <label htmlFor="loan_approved" className="text-sm cursor-pointer">
                  Loan is credit approved
                </label>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/")}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="create-application-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create Application
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Link Beneficiary Dialog */}
      <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Link Existing Bank Account?</DialogTitle>
            <DialogDescription>
              We found verified bank accounts for {formData.company_name}. 
              Would you like to link one to this application?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
            {existingBeneficiaries.map((ben) => (
              <BeneficiaryCard
                key={ben.beneficiary_id}
                beneficiary={ben}
                compact
                selected={selectedBeneficiary?.beneficiary_id === ben.beneficiary_id}
                onSelect={() => setSelectedBeneficiary(ben)}
              />
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setLinkDialog(false);
                navigate(`/workflow/${createdAppId}`);
              }}
            >
              Skip
            </Button>
            <Button
              onClick={handleLinkBeneficiary}
              disabled={!selectedBeneficiary}
              data-testid="link-beneficiary-btn"
            >
              Link & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
